/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';
import SelectionIndicator from '../../status/SelectionIndicator';
import {InfoTooltip} from '../../tooltip/InfoTooltip';

interface AccordionSectionHeaderProps {
  title: string;
  infoTooltip?: string;
  count?: number;
  // rendered immediately after the title/tooltip/count, in the same flex
  // row - e.g. Diagnostics' "time since last report" freshness pill, which
  // per Figma sits right next to the title rather than flush against the
  // accordion's far edge ...
  trailingContent?: ReactNode;
}

const useStyles = makeStyles(() => ({
  container : {
    boxSizing  : 'border-box',
    display    : 'flex',
    alignItems : 'center',
    width      : '100%',
    padding    : '8px 16px 8px 0',
    gap        : '8px'
  },
  title : {
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : '16px',
    lineHeight    : '18px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    color         : dlsColor('grey2')
  }
}));

/**
 * AccordionSectionHeader renders the current theme's shared accordion-section
 * title row - an uppercase title, an optional info tooltip, and an optional
 * numeric count badge. This is the `header` prop content for
 * `AccordionWrapper` (`@ion/dls/src/components/accordion/Accordion`) across
 * every Asset Details Drawer section (Diagnostics, Tags, Vision, Spreader,
 * Additional Info, etc.) - the accordion's own expand/collapse chevron comes
 * from `AccordionWrapper` itself (MUI's `AccordionSummary` `expandIcon`), not
 * from this component.
 *
 * ```typescript
 *
 *   import AccordionWrapper from '@ion/dls/src/components/accordion/Accordion';
 *   import {AccordionSectionHeader} from
 *     '@ion/dls/src/components/accordion/current/AccordionSectionHeader';
 *   ...
 *
 *   <AccordionWrapper isCustomStyle
 *     header={
 *       <AccordionSectionHeader title='Diagnostics'
 *         count={3}
 *         infoTooltip='Shows current diagnostic readings.' />
 *     }
 *     details={<DiagnosticsAccordionBody />} />
 *
 * ```
 *
 */
export const AccordionSectionHeader = ({
  title,
  infoTooltip,
  count,
  trailingContent
}: AccordionSectionHeaderProps) => {
  const classes = useStyles();
  return (
    <div className={classes.container}>
      <span className={classes.title}>
        {title}
      </span>
      {infoTooltip && (
        <InfoTooltip message={infoTooltip}
          size={16}
          color={dlsColor('grey3')} />
      )}
      {typeof count === 'number' && (
        <SelectionIndicator variant='total' count={count} />
      )}
      {trailingContent}
    </div>
  );
};

export default React.memo(AccordionSectionHeader);
